import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { formatDate, formatNumber } from '../../utils/helpers';
import { manualRegistrationsApi } from './manualRegistrationsApi';
import {
  CHANGE_TYPES, HISTORY_STATUSES, PAID_VIA_OPTIONS, changeTypeLabel, formatINR, paidViaLabel,
} from './manualRegistrationUtils';
import { RegistrationStatusBadge } from './ManualRegistrationParts';
import { SectionHeading, StatTile } from './AdminWidgets';
import Pagination from './Pagination';
import { ActivityIcon, BuildingIcon, FileCheckIcon, PlusIcon, ReceiptIcon, SearchIcon } from './ProductAdminIcons';
import styles from './ProductAdmin.module.css';
import ui from './ProductAdminDashboard.module.css';
import t from './AdminTable.module.css';
import m from './ManualRegistrations.module.css';

const PAGE_SIZE = 10;
const NO_FILTERS = { search: '', status: '', paidVia: '', changeType: '' };

// Every paid subscription: payments made through the application and registrations recorded manually
export default function ProductAdminManualRegistrations() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(NO_FILTERS);
  const [query, setQuery] = useState(NO_FILTERS);   // filters as applied (search is debounced)
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Typing in the search box waits a moment; the selects apply straight away
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(filters); setPage(1); }, filters.search === query.search ? 0 : 300);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [list, summary] = await Promise.all([
        manualRegistrationsApi.listPaidHistory({ page, pageSize: PAGE_SIZE, ...query }),
        manualRegistrationsApi.stats(),
      ]);
      setData(list); setStats(summary);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load the paid history');
    } finally { setLoading(false); }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  const hasFilters = Object.values(query).some(Boolean);
  const set = (field) => (event) => setFilters((current) => ({ ...current, [field]: event.target.value }));

  return (
    <div className={ui.container}>
      <header className={m.headerRow}>
        <div className={ui.pageHeader}>
          <h1 className={ui.title}>Manual Registrations</h1>
          <p className={ui.subtitle}>
            All paid subscriptions: payments made through the application and offline payments you register here.
          </p>
        </div>
        <div className={m.headerActions}>
          <Button onClick={() => navigate('/product-admin/manual-registrations/new')}>
            <span className={m.buttonContent}><PlusIcon size={16} /> New Registration</span>
          </Button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <section className={ui.statsGrid}>
        <StatTile tone="blue" title="Paid subscriptions" value={stats ? formatNumber(stats.total_count) : '—'}
          hint="Online and manual, excluding cancelled" icon={<ReceiptIcon />} />
        <StatTile tone="green" title="Amount received" value={stats ? formatINR(stats.total_amount) : '—'}
          hint="Including GST, in INR" icon={<FileCheckIcon />} />
        <StatTile tone="amber" title="Online payments" value={stats ? formatNumber(stats.online_count) : '—'}
          hint={stats ? `${formatINR(stats.online_amount)} through the application` : 'Through the application'} icon={<BuildingIcon />} />
        <StatTile tone="purple" title="Manual registrations" value={stats ? formatNumber(stats.manual_count) : '—'}
          hint={stats ? `${formatINR(stats.manual_amount)} registered by you` : 'Registered by you'} icon={<ActivityIcon />} />
      </section>

      <section className={ui.panel}>
        <SectionHeading
          tone="blue"
          icon={<ReceiptIcon />}
          title="Paid history"
          text={loading ? 'Loading…' : `${formatNumber(data.total)} paid subscription${data.total === 1 ? '' : 's'}${hasFilters ? ' match your filters' : ''}`}
        />

        <div className={m.filters}>
          <label className={`${m.filterField} ${m.filterSearch}`}>
            Search
            <span className={`${ui.searchBox} ${m.searchBoxFull}`}>
              <SearchIcon size={16} />
              <input className={ui.searchInput} value={filters.search} onChange={set('search')}
                placeholder="Company, admin email or reference…" aria-label="Search paid history" />
            </span>
          </label>
          <label className={m.filterField}>
            Status
            <select className={ui.control} value={filters.status} onChange={set('status')}>
              <option value="">All statuses</option>
              {HISTORY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className={m.filterField}>
            Paid via
            <select className={ui.control} value={filters.paidVia} onChange={set('paidVia')}>
              <option value="">All payments</option>
              {PAID_VIA_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className={m.filterField}>
            Type
            <select className={ui.control} value={filters.changeType} onChange={set('changeType')}>
              <option value="">All types</option>
              {Object.entries(CHANGE_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        </div>

        {loading ? <p className={styles.loading}>Loading…</p> : (
          <>
            <div className={t.wrap}>
              <table className={t.table}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th className={t.wPlan}>Plan</th>
                    <th className={`${t.wStat} ${t.num}`}>Amount</th>
                    <th className={`${t.wLimit} ${t.hideSm}`}>Paid via</th>
                    <th className={`${t.wDate} ${t.hideSm}`}>Payment date</th>
                    <th className={`${t.wStatus} ${t.hideXs}`}>Status</th>
                    <th className={t.action} />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className={t.stack}>
                          <span className={t.name} title={item.company_name}>{item.company_name}</span>
                          <span className={t.muted} title={item.admin.email}>{item.admin.email}</span>
                        </div>
                      </td>
                      <td className={t.fit}>
                        <div className={t.stack}>
                          <span title={item.plan_name}>{item.plan_name}</span>
                          <span className={t.muted} title={changeTypeLabel(item.change_type)}>{changeTypeLabel(item.change_type)}</span>
                        </div>
                      </td>
                      <td className={`${t.num} ${t.accent}`} title={formatINR(item.amount)}>{formatINR(item.amount)}</td>
                      <td className={t.hideSm} title={paidViaLabel(item.paid_via)}>{paidViaLabel(item.paid_via)}</td>
                      <td className={t.hideSm} title={formatDate(item.payment_date)}>{formatDate(item.payment_date)}</td>
                      <td className={`${t.fit} ${t.hideXs}`}><RegistrationStatusBadge status={item.status} /></td>
                      <td className={t.action}>
                        <Button size="sm" variant="secondary" className={t.softButton}
                          onClick={() => navigate(`/product-admin/manual-registrations/${item.id}`)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.items.length && (
                <div className={styles.emptyState}>
                  <strong>{hasFilters ? 'No payments match' : 'No paid subscriptions yet'}</strong>
                  <span>{hasFilters ? 'Try different filters.' : 'Payments made through the application and manual registrations appear here.'}</span>
                </div>
              )}
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />
          </>
        )}
      </section>
    </div>
  );
}
